import type { RefObject } from 'react';
import type { AgGridReact } from 'ag-grid-react';

type AGGridScrollPosition = 'top' | 'middle' | 'bottom';

export type UseAGGridScrollToRowArgs = {
  gridRef: RefObject<AgGridReact | null>;
  gridElementRef: RefObject<HTMLElement | null>;
};

export type ScrollToAGGridRowOptions = {
  position?: AGGridScrollPosition;
  block?: ScrollLogicalPosition;
  behavior?: ScrollBehavior;
  flash?: boolean;
};

export const useAGGridScrollToRow = (args: UseAGGridScrollToRowArgs) => {
  const scrollToRow = (
    rowId: string,
    options: ScrollToAGGridRowOptions = {}
  ) => {
    const gridApi = args.gridRef.current?.api;
    const rowNode = gridApi?.getRowNode(rowId);

    if (!gridApi || !rowNode) {
      return false;
    }

    if (rowNode.rowIndex !== null) {
      gridApi.ensureIndexVisible(
        rowNode.rowIndex,
        options.position ?? 'middle'
      );
    }

    window.requestAnimationFrame(() => {
      getGridRowElement({
        gridElement: args.gridElementRef.current,
        rowId,
        rowIndex: rowNode.rowIndex,
      })?.scrollIntoView({
        block: options.block ?? 'center',
        behavior: options.behavior ?? 'smooth',
      });

      if (options.flash) {
        gridApi.flashCells({
          rowNodes: [rowNode],
          flashDuration: 2000,
          fadeDuration: 500,
        });
      }
    });

    return true;
  };

  return { scrollToRow };
};

const getGridRowElement = (args: {
  gridElement: HTMLElement | null;
  rowId: string;
  rowIndex: number | null;
}) => {
  const rowById = Array.from(
    args.gridElement?.querySelectorAll<HTMLElement>('.ag-row[row-id]') ?? []
  ).find((rowElement) => {
    return rowElement.getAttribute('row-id') === args.rowId;
  });

  if (rowById || args.rowIndex === null) {
    return rowById;
  }

  return args.gridElement?.querySelector<HTMLElement>(
    `.ag-row[row-index="${args.rowIndex}"]`
  );
};
